import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { buildCalibrationOverlay } from "../../utils/buildCalibrationOverlay";
import type { CalibrationPoint } from "../../../hooks/useInteractionMode";
import type { UsePageCalibrationArgs, UsePageCalibrationResult } from "./types";

const PICK_TOAST_ID = "page-calibration-pick";

export const usePageCalibration = ({
  selectedPlan,
  calibrationState,
  isPickingCalibration,
  setCalibrationPoint,
  stopCalibrating,
  routeCreationOverlay,
  onSuccess,
}: UsePageCalibrationArgs): UsePageCalibrationResult => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoverPosition, setHoverPosition] = useState<CalibrationPoint | null>(
    null,
  );

  const pdfDistance = useMemo(() => {
    if (calibrationState?.state.phase !== "modal") return 0;
    const { anchor, target } = calibrationState.state;
    return Math.hypot(target.x - anchor.x, target.y - anchor.y);
  }, [calibrationState]);

  const overlay = useMemo(() => {
    if (!calibrationState) return null;
    const calibrationOverlay = buildCalibrationOverlay({
      calibration: calibrationState.state,
      hoverPosition,
    });
    const baseRoutes = (routeCreationOverlay?.routes ?? []).map((route) => ({
      ...route,
      isActive: false,
    }));
    return {
      routes: [...baseRoutes, ...calibrationOverlay.routes],
      hoverPosition: calibrationOverlay.hoverPosition,
      isEditable: false,
    };
  }, [calibrationState, hoverPosition, routeCreationOverlay]);

  const handleMapClick = useCallback(
    (pos: CalibrationPoint) => {
      setCalibrationPoint(pos);
    },
    [setCalibrationPoint],
  );

  const handleMapMouseMove = useCallback(
    (pos: CalibrationPoint | null) => {
      setHoverPosition(
        calibrationState?.state.phase === "picking-target" ? pos : null,
      );
    },
    [calibrationState],
  );

  const submit = useCallback(
    async (realMeters: number) => {
      if (!selectedPlan || calibrationState?.state.phase !== "modal") return;
      if (pdfDistance <= 0 || realMeters <= 0) return;

      const { pageIndex } = calibrationState;
      const pageId = selectedPlan.pageIdByIndex.get(pageIndex);
      if (pageId === undefined) return;

      const metersPerPdfPoint = realMeters / pdfDistance;
      setIsSubmitting(true);
      try {
        await api.updateQuantitySurveyorPageCalibration({
          pageId,
          metersPerPdfPoint,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_PAGE_SCALE_SET, {
          method: "calibrated",
        });
        onSuccess({
          floorPlanId: selectedPlan.floorPlanId,
          pageIndex,
          metersPerPdfPoint,
        });
        toast.success(t("smartSurveyor.pageCalibration.pageScaleSet"));
        stopCalibrating();
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t("smartSurveyor.pageCalibration.failedToSave"),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      selectedPlan,
      calibrationState,
      pdfDistance,
      onSuccess,
      stopCalibrating,
      t,
    ],
  );

  useEffect(() => {
    if (isPickingCalibration) {
      toast.info(t("smartSurveyor.pageCalibration.pickInstructions"), {
        toastId: PICK_TOAST_ID,
        autoClose: false,
      });
      return;
    }
    toast.dismiss(PICK_TOAST_ID);
  }, [isPickingCalibration, t]);

  return {
    pdfDistance,
    isSubmitting,
    submit,
    overlay,
    handleMapClick,
    handleMapMouseMove,
  };
};
